import React from "react";
import { motion } from "framer-motion";
import { BookOpenIcon, CalendarDaysIcon, CircleCheckIcon, CoffeeIcon, ShoppingCartIcon, UtensilsIcon } from "lucide-react";
import { BonusIcon, bonuses, mainProduct } from "../data/protocol";
const ICONS: Record<BonusIcon, typeof UtensilsIcon> = {
  recipes: UtensilsIcon,
  cart: ShoppingCartIcon,
  calendar: CalendarDaysIcon,
  tea: CoffeeIcon
};
const EASE = [0.23, 1, 0.32, 1] as const;
function CheckItem({
  children
}: {
  children: React.ReactNode;
}) {
  return <li className="flex items-start gap-2.5 text-base text-cocoa" data-id="element-253" data-mp="i7nxjwm4hqk4">
      <CircleCheckIcon className="mt-0.5 h-5 w-5 shrink-0 text-leaf-price" aria-hidden="true" data-id="element-254" data-mp="wxbz8yy7ur8d" />
      <span data-id="element-255" data-mp="ryqwosvhrvgr">{children}</span>
    </li>;
}
export function ProtocolContents() {
  return <section aria-labelledby="protocolo-title" className="mt-16" data-id="element-256" data-mp="15y9tipiurkdr">
      <motion.div className="text-center" initial={{
      opacity: 0,
      y: 16
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true,
      margin: "-60px"
    }} transition={{
      duration: 0.3,
      ease: EASE
    }} data-id="element-257" data-mp="15yjkafbq1vki">
        <h2 id="protocolo-title" className="font-serif text-3xl font-semibold leading-tight text-forest sm:text-5xl" data-id="element-258" data-mp="3d0qq4racudub">
          Todo Lo Que Incluye Tu Protocolo Completo
        </h2>
        <p className="mt-3 text-lg text-cocoa-soft" data-id="element-259" data-mp="3mz6jz20ro17i">El stack de transformación nocturna más completo</p>
      </motion.div>

      <motion.article className="relative mt-10 overflow-hidden rounded-3xl border-2 border-cream-dark bg-cream p-6 pt-12 sm:p-8 sm:pt-10" initial={{
      opacity: 0,
      y: 24,
      scale: 0.97
    }} whileInView={{
      opacity: 1,
      y: 0,
      scale: 1
    }} viewport={{
      once: true,
      margin: "-60px"
    }} transition={{
      duration: 0.3,
      ease: EASE
    }} data-id="element-260" data-mp="3r1bdm0r7sxu5">
        <span className="absolute right-0 top-0 rounded-bl-2xl bg-forest px-4 py-2 text-xs font-bold uppercase tracking-wide text-white sm:text-sm" data-id="element-261" data-mp="9m4jch1q8bmk">
          Producto principal
        </span>
        <div className="flex flex-col gap-6 sm:flex-row sm:items-start" data-id="element-262" data-mp="iivpjm9zt6uv">
          <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-2xl bg-white shadow-sm sm:h-24 sm:w-24" data-id="element-263" data-mp="2fmdvrt6y4hbt">
            <BookOpenIcon className="h-10 w-10 text-forest" aria-hidden="true" data-id="element-264" data-mp="2akewovi8o6ko" />
          </div>
          <div className="min-w-0" data-id="element-265" data-mp="2og0sbliz9om2">
            <h3 className="font-serif text-xl font-semibold leading-snug text-forest sm:text-2xl" data-id="element-266" data-mp="2jmo4hdm4hlud">{mainProduct.title}</h3>
            <ul className="mt-4 grid gap-x-8 gap-y-3 sm:grid-cols-2" data-id="element-267" data-mp="2yb14clobzou5">
              {mainProduct.items.map(item => <CheckItem key={item} data-id="element-268" data-mp="2tchi0k5tsb19">{item}</CheckItem>)}
            </ul>
          </div>
        </div>
      </motion.article>

      <div className="mt-10 grid gap-x-8 gap-y-10 md:grid-cols-2" data-id="element-269" data-mp="33b6vm0qov9bp">
        {bonuses.map((bonus, i) => {
        const Icon = ICONS[bonus.icon];
        return <motion.article key={bonus.tag} className="relative flex flex-col rounded-3xl border border-cream-dark bg-white p-6 pt-8 sm:p-7 sm:pt-9" initial={{
          opacity: 0,
          y: 24
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: "-40px"
        }} transition={{
          duration: 0.3,
          delay: i % 2 * 0.06,
          ease: EASE
        }} data-id="element-270" data-mp="1d0twjcdj4dmb" data-mpi={i}>
              <motion.span className="absolute -left-3 -top-4 flex h-14 w-14 items-center justify-center rounded-full bg-gold text-sm font-bold text-forest shadow-md" initial={{
            scale: 0.6,
            opacity: 0
          }} whileInView={{
            scale: 1,
            opacity: 1
          }} viewport={{
            once: true
          }} transition={{
            type: "spring",
            stiffness: 400,
            damping: 14,
            delay: 0.1 + i % 2 * 0.06
          }} data-id="element-271" data-mp="u2im24wk0oh4">
                {bonus.tag}
              </motion.span>
              <div className="flex items-center gap-3" data-id="element-272" data-mp="20iuf5rkirg5u">
                <Icon className="h-7 w-7 shrink-0 text-gold" aria-hidden="true" data-id="element-273" data-mp="20jcgh2m1gkkm" />
                <h3 className="font-serif text-xl font-semibold text-forest sm:text-2xl" data-id="element-274" data-mp="25gclrbb2djao">{bonus.title}</h3>
              </div>
              {bonus.description && <p className="mt-4 text-base leading-relaxed text-cocoa-soft" data-id="element-275" data-mp="gbley34u2pn">{bonus.description}</p>}
              <ul className="mt-4 space-y-2.5" data-id="element-276" data-mp="3rmmkwpshuvev">
                {bonus.items.map(item => <CheckItem key={item} data-id="element-277" data-mp="3nl18f5jvgj57">{item}</CheckItem>)}
              </ul>
            </motion.article>;
      })}
      </div>
    </section>;
}